import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";
import { showToast } from "./ToastSlice";

const initialState = {
  id: "",
  firstname: "",
  lastname: "",
  email: "",
  phonenumbers: [],
  profileImageURL: "",
  contactCount: 0,
  favouriteCount: 0,
  tagCount: 0,
  isLoading: true,
  role: "companyAdmin",
  yeastarExtensionId: null,
  extensionNumber: null,
  sipSecret: null,
  yeastarSignature: null,
  pbxURL: null,
  isLinkusDialerActive: false, // Track if full dialer page is open
  contactStatuses: [], // Contact statuses for this user
  leadStatuses: [], // Lead statuses for this user
};

export const fetchProfile = createAsyncThunk(
  "profile/fetchProfile",
  async (paginationData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/getUser", paginationData);
      localStorage.setItem("userId", response.data.data.id);

      return response.data;
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
      return rejectWithValue(error.response.data);
    }
  }
);
export const deleteTemplate = createAsyncThunk(
  "profile/deleteTemplate",
  async (deleteTemplateData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete("/deleteTemplate", {
        data: deleteTemplateData,
      });
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );

      // Refetch templates to get updated list
      const filters = {
        whatsappTemplatePage: 1,
        whatsappTemplateLimit: 10,
        emailTemplatePage: 1,
        emailTemplateLimit: 10,
      };
      dispatch(fetchProfile(filters));

      return response.data.data;
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
      return rejectWithValue(error.response.data);
    }
  }
);

export const addEditTemplate = createAsyncThunk(
  "profile/addEditTemplate",
  async (templateData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/addEditTemplete", templateData);
      console.log("Add/Edit template response:", response.data);

      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );

      return response.data;
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
      return rejectWithValue(error.response.data);
    }
  }
);

export const editProfile = createAsyncThunk(
  "profile/editProfile",
  async (profileData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put("/editProfile", profileData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("Edit profile response:", response.data);

      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
      return response.data;
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
      return rejectWithValue(error.response.data);
    }
  }
);

export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async (passwordData, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post("/changePassword", passwordData);
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
      return response.data;
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
      return rejectWithValue(error.response.data);
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    resetProfile: (state) => {
      Object.assign(state, initialState);
    },
    setYeastarSignature: (state, action) => {
      state.yeastarSignature = action.payload.signature;
      state.pbxURL = action.payload.pbxURL;
    },
    setLinkusDialerActive: (state, action) => {
      state.isLinkusDialerActive = action.payload;
    },
    updateContactStatuses: (state, action) => {
      state.contactStatuses = action.payload;
    },
    updateLeadStatuses: (state, action) => {
      state.leadStatuses = action.payload;
    },
    setDefaultCallerNumber: (state, action) => {
      state.defaultCallerNumber = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log("Fetched profile data:", action.payload);
        Object.assign(state, action.payload.data);
        state.error = undefined;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error;
      })
      .addCase(editProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log("Fetched profile data:", action.payload);
        Object.assign(state, action.payload.data);
        state.error = undefined;
      })
      .addCase(editProfile.rejected, (state, action) => {
        console.error("Edit profile failed", action.payload);
      })
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        console.error("Change password failed", action.payload);
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        // Handle case where payload might be undefined or not have expected structure
        const payload = action.payload || {};
        const { templateType, template_id } = payload;

        if (!templateType || !template_id) {
          console.warn(
            "Delete template payload missing required fields:",
            payload
          );
          return;
        }

        // Initialize templates structure if not exists
        if (!state.templates) {
          state.templates = {
            whatsappTemplates: {
              whatsappTemplatesData: [],
              whatsappTemplatePagination: { totalTemplates: 0 },
            },
            emailTemplates: {
              emailTemplatesData: [],
              emailTemplatePagination: { totalTemplates: 0 },
            },
          };
        }

        if (templateType === "whatsappTemplate") {
          if (state.templates.whatsappTemplates?.whatsappTemplatesData) {
            state.templates.whatsappTemplates.whatsappTemplatesData =
              state.templates.whatsappTemplates.whatsappTemplatesData.filter(
                (template) => template.whatsappTemplate_id !== template_id
              );
            if (state.templates.whatsappTemplates.whatsappTemplatePagination) {
              state.templates.whatsappTemplates.whatsappTemplatePagination.totalTemplates -= 1;
            }
          }
        }
        if (templateType === "emailTemplate") {
          if (state.templates.emailTemplates?.emailTemplatesData) {
            state.templates.emailTemplates.emailTemplatesData =
              state.templates.emailTemplates.emailTemplatesData.filter(
                (template) => template.emailTemplate_id !== template_id
              );
            if (state.templates.emailTemplates.emailTemplatePagination) {
              state.templates.emailTemplates.emailTemplatePagination.totalTemplates -= 1;
            }
          }
        }
      })
      .addCase(addEditTemplate.fulfilled, (state, action) => {
        const { templateType, data: updatedTemplate } = action.payload;

        // Initialize templates structure if not exists
        if (!state.templates) {
          state.templates = {
            whatsappTemplates: { whatsappTemplatesData: [] },
            emailTemplates: { emailTemplatesData: [] },
          };
        }

        if (templateType === "whatsapp") {
          if (!state.templates.whatsappTemplates) {
            state.templates.whatsappTemplates = { whatsappTemplatesData: [] };
          }

          const existingIndex =
            state.templates.whatsappTemplates.whatsappTemplatesData.findIndex(
              (template) =>
                template.whatsappTemplate_id ===
                updatedTemplate.whatsappTemplate_id
            );

          if (existingIndex !== -1) {
            // Update existing template
            state.templates.whatsappTemplates.whatsappTemplatesData[
              existingIndex
            ] = updatedTemplate;
          } else {
            // Add new template
            state.templates.whatsappTemplates.whatsappTemplatesData.unshift(
              updatedTemplate
            );
          }
        }

        if (templateType === "email") {
          if (!state.templates.emailTemplates) {
            state.templates.emailTemplates = { emailTemplatesData: [] };
          }

          const existingIndex =
            state.templates.emailTemplates.emailTemplatesData.findIndex(
              (template) =>
                template.emailTemplate_id === updatedTemplate.emailTemplate_id
            );

          if (existingIndex !== -1) {
            // Update existing template
            state.templates.emailTemplates.emailTemplatesData[existingIndex] =
              updatedTemplate;
          } else {
            // Add new template
            state.templates.emailTemplates.emailTemplatesData.unshift(
              updatedTemplate
            );
          }
        }
      });
  },
});

export default profileSlice.reducer;
export const {
  resetProfile,
  setYeastarSignature,
  setLinkusDialerActive,
  updateContactStatuses,
  updateLeadStatuses,
  setDefaultCallerNumber,
} = profileSlice.actions;
